/**
 * @file plugins/ecScreening.js
 * @author Jake Stark
 * @desc Early Childhood Screening report plugin

*/

/**
 * @module plugins/ecScreening
 * @desc Early Childhood Screening report plugin
 */

define('plugins/earlyChildhoodScreening',[
        "models", "cardboard/models", "cardboard/collections",  "text!plugins/templates/ecs_overview_table.html!strip",  "text!plugins/templates/ecs_trends_table.html!strip", "cardboard/pluginPieces"
    ],
    function (
        ECLDSModels, CardboardModels, CardboardCollections, ecsOverviewTableTemplate, ecsTrendsTemplate, PluginPieces
        ) {

        console.log("MODULE: Early Childhood Screening");

        var exports = {

            // other names by which this plugin is known. this exists for
            // WebFOCUS shim compatibility
            //aliases: ["demographic", "demographics"],
            options: {
                //noDefaultOrg: true,
                orgDetailPane: false,
                noMapSearch: true,
                noOrgFinderSchool: true,
                orgFinderDistrict: true,
                forcedSummaryFilters: ["FISCAL_YEAR"],
                definitionsECLDS: true
            },

            // the options available to these filters will be populated
            // from the database, via the below data sources
            filters: {
                // no filters!
            },

            // each of these is the configuration that gets passed to a
            // DataBoxPaneView.
            panes: [{
                    title: "Early Childhood Screening Overview",
                    explanationText: "<p>This chart shows children screened by age in a given school year. Ratios reflect those screened in that year. The <a href='https://education.mn.gov/MDE/dse/early/elprog/scr/' target='_blank'>Early Childhood Screening Program</a> includes children screened by districts, some charter schools, as well as comparable screenings submitted by parents from Head Start, Child and Teen Checkups, or public health and clinics. The Early Childhood Screening program targets children between the age of 3 and 4 years and must be completed within the first 30 calendar days of kindergarten or first grade. A school year such as 2015 refers to the period July 1, 2014 through June 30, 2015.</p><p>See \"For More Information\" section below to read more about screening.</p>",
                    openByDefault: true,

                    views: [
                        {
                            type: "template",
                            template: "<h5 class='chartHead'>{{data.heading}}</h5>",
                            dataSourceName: "data",
                            dataPath: "dataSets.overview"
                        },

                        {
                            type: "template",
                            template: "<button style='float:right;' onclick='exportChart(this.parentNode.nextSibling.children[0].id);'>Export to PDF</button>"
                        },
                        {
                            // SETUP
                            type: "pie",
                            dataSourceName: "data",
                            dataSetPath: "dataSets.overview",
                            //dataPath: "dataSets.raceAndEthnicity.data", 
                            // all paths in the
                            // chartable series are
                            // relative to this blob

                            // "pointsWithinRows": given that the data is
                            // two-dimensional (meaning an array of arrays, or
                            // similar structure, implying that we have more than
                            // one series to plot) each series is contained in a
                            // "row", like so:
                            //	 +---------+
                            //	 | X X X X |
                            //	 | . . . . |
                            //	 | . . . . |
                            //	 | . . . . |
                            //	 +---------+
                            dataShape: "pointsWithinRows",

                            // since in this case there's only one dimension, due to
                            // there being only one series to plot, it looks like
                            // this:
                            //	 +---------+
                            //	 | X X X X |
                            //	 +---------+
                            convertDataHashToArray: true,
                            dataDimensions: 1,
                            title: "",
                            exportToPDF: true,
                            hideContext: true,

                            // DISPLAY
                            unitString: "%",
                            unitDec: 1,

                            showLegend: true,
                            dataLabels: false,
                            disableLegendClick: false,

                            // SERIES (only one series, this is a simple graph)
                            series: [{
                                valuePath: "percent",
                                displayNamePath: "slice",
                                longDisplayNamePath: "slice",
                            }],
                            errorConditions: [PluginPieces.standardErrorCondition]
                        },
                        {
                              type: "template",
                              dataSourceName: "data",
                              dataSetPath: "dataSets.overview",
                              template: ecsOverviewTableTemplate,
                              errorConditions: [PluginPieces.silentStandardErrorCondition]
                     } 
                    ]
                }, {
                    title: "Initial Screening Age Trends",
                    openByDefault: false,
                    explanationText: "This chart shows trends of children by age screened over time. Ratios reflect those screened in that year. Screening age trends reflect the number of three-, four-, or five- and six-year-olds screened over subsequent school years. If a child was screened more than one time, only the first screening date is included. The <a href='https://education.mn.gov/MDE/dse/early/elprog/scr/' target='_blank'>The Early Childhood Screening Program</a> targets children between the ages of 3 and 4 years, and must be completed within the first 30 calendar days of kindergarten or first grade. A school year such as 2015 refers to the period July 1, 2014 through June 30, 2015.",
                    views: [{
                            type: "template",
                            template: "NOTE: If a child was screened more than one time, only the first screening date is included in this chart."
                        },
                        {
                            type: "template",
                            template: "<button style='float:right;' onclick='exportChart(this.parentNode.nextSibling.children[0].id);'>Export to PDF</button>"
                        },
                        {
                            // SETUP
                            // one glob = one line series
                            // in each series, one row = one data point
                            type: "line",
                            dataSourceName: "data",
                            dataSetPath: "dataSets.trends",

                            // "pointsWithinRows": given that the data is
                            // two-dimensional (meaning an array of arrays, or
                            // similar structure, implying that we have more than
                            // one series to plot) each series is contained in a
                            // "row", like so:
                            //	 +---------+
                            //	 | X X X X |
                            //	 | . . . . |
                            //	 | . . . . |
                            //	 | . . . . |
                            //	 +---------+
                            dataShape: "pointsWithinRows",
                            dataDimensions: 2,

                            // DISPLAY
                            xAxisLabelPath: "year",
                            unitString: "%",
                            exportToPDF: true,
                            hideContext: true,
                            // yAxisTickInterval: 4,
                            showLegend: true,

                            // SERIES
                            series: [{
                                seriesDisplayNamePath: "screeningAge",
                                dataPath: "years",
                                valuePath: "percent"
                            }],
                            errorConditions: [PluginPieces.standardErrorCondition]
                        },
                        
                        {
                            type: "template",
                            template: ecsTrendsTemplate,
                            dataSourceName: "data",
                            dataSetPath: "dataSets.trends",
                            errorConditions: [PluginPieces.silentStandardErrorCondition]
                        } 
                    ]
                },

                {
                    title: "For More Information",
                    visibleByDefault: true,
                    openByDefault: false,
                    views: [{
                        type: "template",
                        template: " <h2> Glossary</h2><p><strong> Early Childhood Screening (ECS)</strong>:  ECS is a legislatively-required program that leads to earlier health and education interventions, if needed, and better outcomes for children. ECS is often a parent's first encounter with a school district and may be the first time they hear about or are referred to early learning programs; early childhood scholarships; Early Childhood Family Education; community programs; and medical, mental health, or special education services. The free screening is required for children attending public school kindergarten or first grade in Minnesota.  <a href='https://education.mn.gov/MDE/dse/early/elprog/scr/' target='_blank'>The Early Childhood Screening Program</a> includes children screened by districts, some charter schools, as well as copies of comparable screenings submitted by parents from Head Start, Child and Teen Checkups, or public health and clinics. The included factors do not contain all known factors which may impact a child receiving screening such as lack of affordable transportation, homelessness/high mobility, or child/family health concerns. </p><p><strong>Maternal Education</strong>: The mother's educational attainment at the time of the child's birth and data is identified in the child's birth record. Maternal Education is unknown for children born outside of Minnesota or children who were missing this data on their birth record (e.g. no birth data available).</p><p><strong>Minnesota District Preschool</strong>: Districts provide preschool services in a number of formats for children age three to kindergarten entrance with the purpose of preparing them to enter kindergarten. As of June 2021, Voluntary Prekindergarten (VPK) and School Readiness Plus (SRP) data are included in the MN District Preschool data provided by MDE. School Readiness data begin with the 2013-2014 school year; VPK data begin with the 2016-2017 school year; SRP data begin with the 2017-2018 school year.</p><p><strong>Race/Ethnicity</strong>: Federal Race/Ethnicity categories are as reported by the child's education record and align with the federal <a href='https://www.govinfo.gov/content/pkg/FR-1997-10-30/pdf/97-28653.pdf' target='_blank'>Office of Management and Budget's standards</a>. <i>Note: While state race data is collected in K-12 education records and utilized in ECLDS Kindergarten and Third Grade reports, the same data is not yet universally collected by early care and education programs.</i></p> "
                    }]
                }
            ],

            filterSources: {
                orgs: {
                    modelType: ECLDSModels.ECLDSOrgList,
                    //url: "/data/state.json",
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_orgid_ecScreening.fex",
                    noViewParams: true,
                    enabled: true
                },
                filters: {
                    modelType: CardboardModels.FilterProducer,
                    filterSources: ["filterForReport"],
                    urls: {
                        filterForReport: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_ecScreening_EcoDevRegion.fex"
                    },
                    enabled: true,
                    noViewParams: false
                },
                filters2: {
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_fiscal_year_ecScreening.fex",
                    enabled: true,
                    modelType: CardboardModels.FilterData
                },

                filters4: {
                    modelType: CardboardModels.FilterProducer,
                    filterSources: ["filterForReport"],
                    urls: {
                        filterForReport: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_ecScreening.fex"
                    },

                    enabled: true,
                    noViewParams: false
                },
                filters3: {
                    modelType: CardboardModels.FilterProducer,
                    filterSources: ["filterForReport"],
                    urls: {
                        filterForReport: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_ecScreening_nbr2.fex"
                    },

                    enabled: true,
                    noViewParams: false
                },

                demographicCategories: {
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_ecScreening_nbr3",
                    enabled: true,
                    modelType: CardboardCollections.DemoCatProducer,
                    noViewParams: false
                }
            },

            dataSources: {
                data: {
                    modelType: CardboardModels.Model,
                    //url:"/data/tempChildDemo.json",
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_sec_z2_ecScreening",
                    yearPath: "meta.fiscalYear", // where to look in the JSON for the year
                    //	defaultParams: {
                    //		orgId: 999999000,
                    //      COHORTID: 2014
                    //	},
                    enabled: true
                }
            }
        };

        return exports;
    });
